import * as PegParser from './parser/peg-cfi-parser';
import CfiUtils from 'cfi-utils';

class CfiParser {

  constructor(docNode, options) {
    if (docNode.documentElement) {
      this.doc = docNode;
    } else {
      throw new TypeError('The top level node must be a document.');
    }

    this.pegParser = PegParser;
    this.utils = new CfiUtils(options);
  }

  parse(cfiString) {
    return this.pegParser.parse(cfiString);
  }

  getElementByCfi(cfiString) {
    let parsedCfi = this.pegParser.parse(cfiString);
    let startElement = this.doc.documentElement;
    let startStepNumber = this._getFirstIndirectionStepNum(parsedCfi);
    let offset = parsedCfi.cfiString.localPath.termStep.offsetValue;

    if (startStepNumber === 0) {
      startElement = this.doc.body;
    }

    return this._getElementByLocalPath(parsedCfi.cfiString.localPath, startStepNumber, {'node': startElement}, offset);
  }

  getRangeByCfi(cfiString) {
    let parsedCfi = this.pegParser.parse(cfiString);

    if (!(parsedCfi.cfiString.range1 && parsedCfi.cfiString.range2)) {
      throw new TypeError('getRangeByPartialCfi requires a cfi string representing a range');
    }

    // start at the html element.
    let startElement = this.doc.documentElement;
    let startStepNumber = this._getFirstIndirectionStepNum(parsedCfi);

    if (startStepNumber === 0) {
      // if there is no indirection step then start at the body element.
      startElement = this.doc.body;
    }

    let commonAncestor = this._getElementByLocalPath(
      parsedCfi.cfiString.localPath, startStepNumber, {'node': startElement}
    );

    let anchorOffset = parseInt(parsedCfi.cfiString.range1.termStep.offsetValue, 10) || 0;
    let focusOffset = parseInt(parsedCfi.cfiString.range2.termStep.offsetValue, 10) || 0;

    let anchorElement = this._getElementByLocalPath(parsedCfi.cfiString.range1, 0, commonAncestor, anchorOffset);
    let focusElement = this._getElementByLocalPath(parsedCfi.cfiString.range2, 0, commonAncestor, focusOffset);

    return {
      anchorElement: anchorElement.node,
      anchorOffset: anchorElement.offset,
      focusElement: focusElement.node,
      focusOffset: focusElement.offset
    };
  }

  _getFirstIndirectionStepNum(parsedCfi) {
    let stepNum = 0;

    if (parsedCfi.cfiString.localPath.steps) {
      for (stepNum; stepNum <= parsedCfi.cfiString.localPath.steps.length - 1; stepNum++) {
        let nextStepNode = parsedCfi.cfiString.localPath.steps[stepNum];

        if (nextStepNode.type === 'indirectionStep') {
          return stepNum;
        }
      }
    }

    return 0;
  }

  _getElementByLocalPath(localPath, startStep, currentElement, offset) {

    if (localPath !== '') {
      for (let i = startStep; i < localPath.steps.length; i++) {
        let step = localPath.steps[i];

        if (step.type === 'indexStep') {
          currentElement = this._getIndexStepNode(step, currentElement, offset);
        }
        if (step.type === 'indirectionStep') {
          currentElement = this._getIndirectionStepNode(step, currentElement);
        }
      }
    }

    return currentElement;
  }

  _getIndexStepNode(indexStep, currentElement, offset) {
    if (indexStep === undefined || indexStep.type !== 'indexStep') {
      throw new TypeError('_getIndexStepNode requires an indexStep');
    }

    currentElement = this._getNodeByStep(indexStep.stepLength, currentElement, offset);

    if (indexStep.idAssertion) {
      if (currentElement.node.id !== indexStep.idAssertion) {
        throw new Error('id assertion failed.');
      }
    }

    return currentElement;
  }

  _getIndirectionStepNode(indirectionStep, currentElement) {
    if (indirectionStep === undefined || indirectionStep.type !== 'indirectionStep') {
      throw new TypeError('_getIndirectionStepNode requires and indirectionStep');
    }

    // transform to an index step since we are not loading external content.
    indirectionStep.type = 'indexStep';

    return this._getIndexStepNode(indirectionStep, currentElement);

  }

  _getNodeByStep(step, currentElement, offset) {
    let targetNode;
    let stepNum = parseInt(step, 10);
    let childNodes = [];
    let sortedNodes = [];
    let lastNodeBlacklisted = false;
    let lastNodeTextNode = false;
    let currentNode = currentElement.node;

    for (let i = 0; i < currentNode.childNodes.length; i++) {
      childNodes.push(currentNode.childNodes[i]);
    }

    // fixes a bug in some browsers where tags like <div><p> don't include an empty text node.
    for (let x = 0; x < childNodes.length; x += 2) {
      if (childNodes[x].nodeType === Node.ELEMENT_NODE) {
        childNodes.splice(x, 0, {nodeType: Node.TEXT_NODE});
      }
    }

    for (let i = 0; i < childNodes.length; i++) {
      let node = childNodes[i];

      if (lastNodeBlacklisted) {
        if (!(this.utils.isBlacklisted(node))) {
          let previousChild = sortedNodes.pop();

          if (previousChild && previousChild.constructor === Array) {
            previousChild.push(node);
            sortedNodes.push(previousChild);
          } else if (previousChild) {
            sortedNodes.push([previousChild, node]);
          } else {
            sortedNodes.push(node);
          }
          lastNodeBlacklisted = false;
        }
      } else if (lastNodeTextNode && node.nodeType === Node.TEXT_NODE) {
        let previousChild = sortedNodes.pop();

        if (previousChild && previousChild.constructor === Array) {
          previousChild.push(node);
          sortedNodes.push(previousChild);
        } else if (previousChild) {
          sortedNodes.push([previousChild, node]);
        } else {
          sortedNodes.push(node);
        }
      } else if (this.utils.isBlacklisted(node)) {
        lastNodeBlacklisted = true;
      } else if (node.nodeType === Node.TEXT_NODE) {
        lastNodeTextNode = true;
        sortedNodes.push(node);
      } else {
        lastNodeTextNode = false;
        sortedNodes.push(node);
      }
    }

    let index = stepNum - 1;

    if (stepNum % 2 === 0) {
      targetNode = sortedNodes[index];
    } else {
      let textChild = sortedNodes[index];

      if (textChild.constructor === Array) {
        let currentChars = 0;

        for (let i = 0; i < textChild.length; i++) {
          let textNode = textChild[i];

          if (currentChars + textNode.length >= offset) {
            offset -= currentChars;
            targetNode = textNode;
            break;
          }
          if (textNode.textContent) {
            currentChars += textNode.textContent.length;
          }
        }
      } else {
        targetNode = textChild;
      }
    }

    return {'node': targetNode, 'offset': offset};
  }
}

export default CfiParser;



// WEBPACK FOOTER //
// ./src/cfi-parser.js